/**
 * Odoo-Style Kanban Card
 *
 * Compact card design matching Odoo's permit tracker:
 * - Title + optional description
 * - Colored tags (many visible)
 * - Due date with relative time
 * - Priority star, time tracking icon
 * - Subtask progress indicator
 */

import { useMemo } from 'react';
import { Typography, Tooltip } from 'antd';
import { StyledTag } from '@components/common';
import {
  StarFilled,
  StarOutlined,
  ClockCircleOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons';
import type { ProcessInstance } from '@/types';
import type { Stage } from './OdooStyleKanbanBoard';

const { Text } = Typography;

interface OdooStyleKanbanCardProps {
  task: ProcessInstance;
  stage?: Stage;
  onClick: () => void;
}

export default function OdooStyleKanbanCard({ task, stage: _stage, onClick }: OdooStyleKanbanCardProps) {
  // Calculate relative due date
  const dueDateInfo = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(task.dueDate);
    due.setHours(0, 0, 0, 0);
    const diff = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    let text: string;
    let color: string;
    let isOverdue = false;

    if (diff === 0) {
      text = 'Today';
      color = '#f97316'; // Orange
    } else if (diff === 1) {
      text = 'Tomorrow';
      color = '#f97316';
    } else if (diff > 1 && diff <= 7) {
      text = `In ${diff} days`;
      color = '#6b7280'; // Gray
    } else if (diff > 7) {
      // Show date for far future
      text = due.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      color = '#6b7280';
    } else if (diff === -1) {
      text = 'Yesterday';
      color = '#ef4444';
      isOverdue = true;
    } else {
      // Past date
      text = `${Math.abs(diff)} days ago`;
      color = '#ef4444';
      isOverdue = true;
    }

    return { text, color, isOverdue, date: due };
  }, [task.dueDate]);

  // Get tags from task (could be on template or directly on task)
  const tags = useMemo(() => {
    const taskTags = (task as any).tags || task.template?.tags || [];
    return taskTags;
  }, [task]);

  // Priority (from extended task properties)
  const isPriority = useMemo(() => {
    return (task as any).priority === 1 || (task as any).is_priority;
  }, [task]);

  // Subtask progress
  const subtaskProgress = useMemo(() => {
    const subtasks = (task as any).subtasks || [];
    const completed = subtasks.filter((s: any) => s.is_completed).length;
    const total = subtasks.length;
    return { completed, total, hasSubtasks: total > 0 };
  }, [task]);

  // Get display title and description
  const title = task.template?.title || (task as any).name || 'Untitled';
  const description = task.template?.description || (task as any).description || '';

  return (
    <div
      className="odoo-kanban-card"
      onClick={onClick}
      style={{
        borderLeft: dueDateInfo.isOverdue ? '3px solid #ef4444' : undefined,
      }}
    >
      {/* Title */}
      <div className="card-title">
        <Text strong style={{ fontSize: 13, lineHeight: 1.3 }}>
          {title}
        </Text>
      </div>

      {/* Description (if short, show first line) */}
      {description && (
        <div className="card-description">
          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.2 }}>
            {description.length > 50 ? `${description.substring(0, 50)}...` : description}
          </Text>
        </div>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <div className="card-tags" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
          {tags.map((tag: any, idx: number) => (
            <StyledTag
              key={tag.id || idx}
              name={tag.name}
              color={tag.color || '#e5e7eb'}
              style={{ margin: 0 }}
            />
          ))}
        </div>
      )}

      {/* Due Date */}
      <div className="card-due-date" style={{ color: dueDateInfo.color }}>
        {dueDateInfo.text}
      </div>

      {/* Footer with icons */}
      <div className="card-footer">
        <div className="footer-icons">
          {/* Priority Star */}
          <Tooltip title={isPriority ? 'Priority' : 'Set priority'}>
            {isPriority ? (
              <StarFilled style={{ color: '#facc15', fontSize: 14 }} />
            ) : (
              <StarOutlined style={{ color: '#d1d5db', fontSize: 14 }} />
            )}
          </Tooltip>

          {/* Time tracking */}
          <Tooltip title="Time tracking">
            <ClockCircleOutlined style={{ color: '#9ca3af', fontSize: 14 }} />
          </Tooltip>

          {/* Subtask progress */}
          {subtaskProgress.hasSubtasks && (
            <Tooltip title={`${subtaskProgress.completed}/${subtaskProgress.total} subtasks`}>
              <span className="subtask-progress">
                <CheckSquareOutlined style={{ color: '#9ca3af', fontSize: 14, marginRight: 2 }} />
                <span style={{ fontSize: 11, color: '#6b7280' }}>
                  {subtaskProgress.completed}/{subtaskProgress.total}
                </span>
              </span>
            </Tooltip>
          )}
        </div>

        {/* Progress circle indicator */}
        <div className="progress-indicator">
          <svg width="18" height="18" viewBox="0 0 18 18">
            <circle
              cx="9"
              cy="9"
              r="7"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="2"
            />
            {task.status === 'completed' && (
              <circle
                cx="9"
                cy="9"
                r="7"
                fill="none"
                stroke="#22c55e"
                strokeWidth="2"
                strokeDasharray={`${2 * Math.PI * 7}`}
                strokeDashoffset="0"
              />
            )}
            {task.status === 'in_progress' && (
              <circle
                cx="9"
                cy="9"
                r="7"
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2"
                strokeDasharray={`${2 * Math.PI * 7}`}
                strokeDashoffset={`${2 * Math.PI * 7 * 0.5}`}
                transform="rotate(-90 9 9)"
              />
            )}
          </svg>
        </div>
      </div>
    </div>
  );
}
